import { useState, useEffect } from 'react';
import { useDatabaseCourseNavigation } from './useDatabaseCourseNavigation';
import { useAssignedCoursesOnly } from './useAssignedCoursesOnly';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

interface CourseCountData {
  totalCourses: number;
  totalCategories: number;
  coursesByCategory: Record<string, number>;
  lastUpdated: number;
}

interface UseUnifiedCourseCountsResult extends CourseCountData {
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for unified course counts
 * - Authenticated users: Uses database navigation (full data)
 * - Unauthenticated users: Uses public RPC function (counts only)
 */
export const useUnifiedCourseCounts = (): UseUnifiedCourseCountsResult => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const { userRole } = useSupabaseAuth();
  
  // Wait for role to be determined before calculating isStudent
  useEffect(() => {
    if (userRole !== undefined && userRole !== null) {
      setRoleLoading(false);
    } else if (isAuthenticated === false) {
      // Not authenticated, no role to load
      setRoleLoading(false);
    }
  }, [userRole, isAuthenticated]);
  
  // Determine if user is a student (should see only assigned courses)
  const isStudent = userRole === 'user' || userRole === null;
  
  console.log('📊 [useUnifiedCourseCounts] Hook state:', {
    isAuthenticated,
    userRole,
    isStudent,
    roleLoading
  });
  
  // For students - filtered courses based on assignments
  const { 
    courses: assignedCourses,
    loading: assignedLoading,
    error: assignedError,
    refetch: refetchAssigned
  } = useAssignedCoursesOnly();
  
  // For admins/staff - full database navigation (all courses)
  const { 
    categories, 
    courses: allCourses, 
    loading: dbLoading, 
    error: dbError,
    refetch: refetchAll 
  } = useDatabaseCourseNavigation();
  
  // Choose the appropriate course list based on role (only after role is loaded)
  const shouldUseAssignedCourses = isAuthenticated && !roleLoading && isStudent;
  
  console.log('🎯 [useUnifiedCourseCounts] Data source selection:', {
    shouldUseAssignedCourses,
    isAuthenticated,
    isStudent,
    roleLoading,
    assignedCoursesCount: assignedCourses.length,
    allCoursesCount: allCourses.length
  });
  
  const courses = shouldUseAssignedCourses ? assignedCourses : allCourses;
  const coursesLoading = shouldUseAssignedCourses ? assignedLoading : dbLoading;
  const coursesError = shouldUseAssignedCourses ? assignedError : dbError;
  const refetch = shouldUseAssignedCourses ? refetchAssigned : refetchAll;
  
  // Calculate unique categories from assigned courses for students
  const uniqueCategories = shouldUseAssignedCourses 
    ? Array.from(new Set(assignedCourses.map(c => c.category).filter(Boolean)))
    : categories;
    
  console.log('📈 [useUnifiedCourseCounts] Final counts:', {
    totalCourses: courses.length,
    totalCategories: shouldUseAssignedCourses ? uniqueCategories.length : categories.length,
    loading: coursesLoading
  });

  // For unauthenticated users - public counts
  const [publicCounts, setPublicCounts] = useState<CourseCountData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch public counts for unauthenticated users
  const fetchPublicCounts = async () => {
    console.log('[CourseCount] Fetching public counts, isAuthenticated:', isAuthenticated);
    if (isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      console.log('[CourseCount] Calling RPC get_public_course_counts...');
      const { data, error: rpcError } = await supabase.rpc('get_public_course_counts');
      
      console.log('[CourseCount] RPC Response:', { data, error: rpcError });

      if (rpcError) throw rpcError;

      const result = data as { total_courses: number; total_categories: number; courses_by_category: Record<string, number> };

      setPublicCounts({
        totalCourses: result.total_courses,
        totalCategories: result.total_categories,
        coursesByCategory: result.courses_by_category || {},
        lastUpdated: Date.now()
      });
    } catch (err) {
      console.error('Failed to fetch public course counts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load course counts');
      
      // Fallback to static counts if RPC fails
      setPublicCounts({
        totalCourses: 192,
        totalCategories: 31,
        coursesByCategory: {},
        lastUpdated: Date.now()
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Trigger fetch when we know user is NOT authenticated (false or still checking)
    if (isAuthenticated === false || isAuthenticated === null) {
      fetchPublicCounts();
    }
  }, [isAuthenticated]);

  // Calculate counts based on authentication status and role
  const data: CourseCountData = isAuthenticated
    ? shouldUseAssignedCourses
      ? {
          // For students - count based on assigned courses only
          totalCourses: courses.length,
          totalCategories: uniqueCategories.length,
          coursesByCategory: courses.reduce((acc, course) => {
            if (course.category) {
              acc[course.category] = (acc[course.category] || 0) + 1;
            }
            return acc;
          }, {} as Record<string, number>),
          lastUpdated: Date.now()
        }
      : {
          // For admins/staff - show all courses
          totalCourses: courses.length,
          totalCategories: categories.length,
          coursesByCategory: categories.reduce((acc, category) => {
            acc[category.id] = category.courseCount;
            return acc;
          }, {} as Record<string, number>),
          lastUpdated: Date.now()
        }
    : publicCounts || {
        totalCourses: 192,  // Static fallback from database
        totalCategories: 53, // Static fallback from database
        coursesByCategory: {},
        lastUpdated: Date.now()
      };

  const refresh = async () => {
    if (isAuthenticated) {
      setLoading(true);
      setError(null);
      try {
        await refetch();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to refresh course counts');
      } finally {
        setLoading(false);
      }
    } else {
      await fetchPublicCounts();
    }
  };

  useEffect(() => {
    if (coursesError) {
      setError(coursesError);
    }
  }, [coursesError]);

  // Determine if data is actually ready to display
  const isDataReady = isAuthenticated
    ? (!roleLoading && !coursesLoading) // Authenticated: wait for role AND course data
    : (publicCounts !== null); // Unauthenticated: wait for public counts
  
  console.log('✅ [useUnifiedCourseCounts] Final state:', {
    isDataReady,
    loading: !isDataReady,
    totalCourses: data.totalCourses,
    totalCategories: data.totalCategories
  });

  return {
    ...data,
    loading: !isDataReady, // Keep loading until everything is ready
    error: isAuthenticated ? (error || coursesError) : error,
    refresh
  };
};